import React from "react";
import { Link } from "react-router-dom";

export default function AboutPage() {
    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
            {/* Hero */}
            <section className="text-center mb-16">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 sm:p-12">
                    <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                        About OmniWorth
                    </h1>
                    <p className="text-xl text-gray-700 max-w-2xl mx-auto">
                        We believe everyone deserves to understand their complete financial picture
                        without the complexity.
                    </p>
                </div>
            </section>

            {/* Mission */}
            <section className="mb-16">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 sm:p-12">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Mission</h2>
                    <div className="space-y-4 text-gray-700 leading-relaxed">
                        <p>
                            Managing money shouldn't require a finance degree or hours of manual spreadsheet work.
                            Yet for most people, understanding their complete financial situation means logging into
                            a half-dozen apps, trying to remember which account is where, and piecing together
                            a mental picture that's always out of date.
                        </p>
                        <p>
                            OmniWorth was built to solve this problem. We aggregate all your financial accounts
                            into one clean, secure dashboard so you can see exactly where you stand at any moment.
                            No manual entry. No spreadsheets. Just clarity.
                        </p>
                    </div>
                </div>
            </section>

            {/* Built By */}
            <section className="mb-16">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Who's Behind OmniWorth</h2>
                    <div className="flex flex-col sm:flex-row gap-6 items-start">
                        <div className="w-32 h-32 rounded-full overflow-hidden shrink-0">
                            <img
                                src="/founder.jpeg"
                                alt="Jake Marotta"
                                className="w-full h-full object-cover object-[35%_30%] scale-[1.8] pointer-events-none select-none"
                                draggable="false"
                                onContextMenu={(e) => e.preventDefault()}
                            />
                        </div>
                        <div>
                            <p className="text-gray-700 leading-relaxed">
                                OmniWorth is built by Jake Marotta, a software engineer based in Florida. I built OmniWorth
                                because I wanted a tool focused on growing wealth and tracking my own path to financial
                                independence, not just another budgeting app. As a solo developer, I use OmniWorth myself
                                every day to track my own finances and I'm constantly improving it based on real usage and
                                user feedback.
                            </p>
                            <a
                                href="https://www.linkedin.com/in/jakemarotta"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-accent-600 hover:text-accent-700 text-sm inline-flex items-center gap-1.5 mt-4"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <rect width="24" height="24" rx="4" fill="#0A66C2"/>
                                    <path d="M7.5 8.5a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5zM6.25 10h2.5v8h-2.5v-8zM11 10h2.4v1.1h.034c.334-.633 1.15-1.3 2.366-1.3 2.53 0 3 1.664 3 3.828V18h-2.5v-3.875c0-.924-.016-2.113-1.288-2.113-1.289 0-1.487 1.007-1.487 2.047V18H11v-8z" fill="#ffffff"/>
                                </svg>
                                Connect with me on LinkedIn
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* The Problem We Solve */}
            <section className="mb-16">
                <div className="grid md:grid-cols-2 gap-8">
                    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
                        <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center mb-4 text-gray-700">
                            <svg className="w-5 h-5" stroke="currentColor" strokeWidth="1.75" fill="none" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">The Old Way</h3>
                        <ul className="space-y-2 text-gray-500">
                            <li>• Log into 5+ different banking apps</li>
                            <li>• Manually track balances in spreadsheets</li>
                            <li>• Guess at your total net worth</li>
                            <li>• Miss the big picture on investments</li>
                            <li>• Spend hours reconciling accounts</li>
                        </ul>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
                        <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center mb-4 text-gray-700">
                            <svg className="w-5 h-5" stroke="currentColor" strokeWidth="1.75" fill="none" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">The OmniWorth Way</h3>
                        <ul className="space-y-2 text-gray-500">
                            <li>• One dashboard for all accounts</li>
                            <li>• Automatic sync keeps data fresh</li>
                            <li>• Real-time net worth calculation</li>
                            <li>• All investments in one view</li>
                            <li>• Minutes to set up, seconds to check</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Who It's For */}
            <section className="mb-16">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 sm:p-12">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Who OmniWorth Is For</h2>
                    <div className="grid sm:grid-cols-2 gap-6">
                        <div className="card p-5">
                            <h4 className="font-semibold text-gray-900 mb-2">Young Professionals</h4>
                            <p className="text-gray-500 text-sm">
                                Building wealth across multiple accounts and want to track progress toward financial goals.
                            </p>
                        </div>
                        <div className="card p-5">
                            <h4 className="font-semibold text-gray-900 mb-2">Investors</h4>
                            <p className="text-gray-500 text-sm">
                                Managing portfolios across multiple brokerages and need a unified view of performance.
                            </p>
                        </div>
                        <div className="card p-5">
                            <h4 className="font-semibold text-gray-900 mb-2">Savers</h4>
                            <p className="text-gray-500 text-sm">
                                Working toward savings goals and want to see net worth grow over time.
                            </p>
                        </div>
                        <div className="card p-5">
                            <h4 className="font-semibold text-gray-900 mb-2">Anyone Seeking Clarity</h4>
                            <p className="text-gray-500 text-sm">
                                If you've ever wondered "how much am I actually worth?" - this is for you.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Security & Privacy */}
            <section className="mb-16">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 sm:p-12">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Security & Privacy</h2>
                    <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                        <p>
                            We take security seriously. Your financial data is sensitive, and we've built OmniWorth
                            from the ground up to protect it.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center shrink-0 text-gray-700">
                                <svg className="w-5 h-5" stroke="currentColor" strokeWidth="1.75" fill="none" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900">Plaid-Powered Connections</h4>
                                <p className="text-gray-500 text-sm mt-1">
                                    We never see your bank passwords. Plaid handles authentication directly with your bank.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center shrink-0 text-gray-700">
                                <svg className="w-5 h-5" stroke="currentColor" strokeWidth="1.75" fill="none" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900">256-bit AES Encryption</h4>
                                <p className="text-gray-500 text-sm mt-1">
                                    All sensitive data is encrypted at rest and in transit using bank-grade encryption.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center shrink-0 text-gray-700">
                                <svg className="w-5 h-5" stroke="currentColor" strokeWidth="1.75" fill="none" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900">Read-Only Access</h4>
                                <p className="text-gray-500 text-sm mt-1">
                                    We can only view your account data. No one can move money or make transactions.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center shrink-0 text-gray-700">
                                <svg className="w-5 h-5" stroke="currentColor" strokeWidth="1.75" fill="none" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900">You Control Your Data</h4>
                                <p className="text-gray-500 text-sm mt-1">
                                    Disconnect accounts or delete your data at any time. Your data, your choice.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="text-center">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 sm:p-12">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                        Ready to Get Started?
                    </h2>
                    <p className="text-gray-700 mb-8 max-w-xl mx-auto">
                        Join OmniWorth today and finally get the complete picture of your finances.
                        It only takes a few minutes to connect your accounts.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/signup" className="btn-primary px-8 py-3 text-base font-semibold rounded-lg">
                            Start 14-Day Free Trial
                        </Link>
                        <Link to="/" className="btn-secondary px-8 py-3 text-base font-semibold rounded-lg">
                            Back to Home
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
